import React, { useRef } from 'react'
import { useReactToPrint } from 'react-to-print'
import { CheckCircle, AlertTriangle, Printer, Download, PhoneCall } from 'lucide-react'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { PageAuditResult } from '../types'
import ScoreDisplay from './ScoreDisplay'
import SEOChart from './SEOChart'

interface SEOAuditProps {
  pageResults: PageAuditResult[]
}

const SEOAudit: React.FC<SEOAuditProps> = ({ pageResults }) => {
  const componentRef = useRef(null)

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  })

  const handleDownloadPDF = async () => {
    const element = componentRef.current
    if (element) {
      const canvas = await html2canvas(element)
      const data = canvas.toDataURL('image/png')

      const pdf = new jsPDF()
      const imgProperties = pdf.getImageProperties(data)
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width

      pdf.addImage(data, 'PNG', 0, 0, pdfWidth, pdfHeight)
      pdf.save('seo-audit-report.pdf')
    }
  }

  const overallScore = {
    score: pageResults[0].score,
    maxScore: 10,
  }

  return (
    <div ref={componentRef} className="bg-mover-darkest bg-opacity-20 backdrop-blur-md rounded-lg p-4 sm:p-6 mb-8 w-full max-w-4xl text-mover-lightest">
      <h2 className="text-2xl font-bold mb-4 text-mover-lightest">
        SEO Audit Results for {pageResults[0].url}
      </h2>
      <ScoreDisplay score={overallScore} />
      <SEOChart pageResults={pageResults} />
      {pageResults[0].results.map((result, index) => (
        <div key={index} className="mb-6 p-4 bg-mover-dark bg-opacity-50 rounded-lg">
          <h3 className="text-xl font-semibold mb-2 flex items-center">
            {result.passed ? (
              <CheckCircle className="w-6 h-6 text-mover-cyan mr-2 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-mover-magenta mr-2 flex-shrink-0" />
            )}
            {result.name}
          </h3>
          <p className="mb-2">{result.details}</p>
          <div className="mb-2">
            <span className="font-semibold">Score: </span>
            <span className={result.score >= 7 ? 'text-mover-cyan' : 'text-mover-magenta'}>
              {result.score.toFixed(1)}/10
            </span>
          </div>
          {result.recommendations && result.recommendations.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Recommendations:</h4>
              <ul className="list-disc list-inside space-y-1">
                {result.recommendations.map((recommendation, recIndex) => (
                  <li key={recIndex}>{recommendation}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0 sm:space-x-4">
        <a
          href="https://www.movermarketing.ai/book-a-call?utm_source=online&utm_medium=seoaudit&utm_campaign=seoaudit&utm_id=seoaudit"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary rounded-lg flex items-center justify-center px-4 py-3 w-full sm:w-auto"
        >
          <PhoneCall className="w-5 h-5 mr-2" />
          <span>Get a Free SEO Consultation</span>
        </a>
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
          <button onClick={handlePrint} className="btn-primary rounded-lg flex items-center justify-center px-4 py-3 w-full sm:w-auto">
            <Printer className="w-5 h-5 mr-2" />
            <span>Print Report</span>
          </button>
          <button onClick={handleDownloadPDF} className="btn-primary rounded-lg flex items-center justify-center px-4 py-3 w-full sm:w-auto">
            <Download className="w-5 h-5 mr-2" />
            <span>Download PDF</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default SEOAudit